import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faUserMd, faDesktop } from '@fortawesome/free-solid-svg-icons';
import { AtendimentoRole } from '../types';
import Button from '../components/Button';
import PageHeader from '../components/PageHeader';

const ROLE_STORAGE_KEY = 'atendimento-role';

const AtendimentosPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedRole, setSelectedRole] = useState<AtendimentoRole | null>(null);

  const isRoleSubRoute = useCallback(() => {
    const path = location.pathname;
    return path.includes('/fila') || path.includes('/consultorio') || path.includes('/monitor');
  }, [location.pathname]);

  const navigateToRole = useCallback(
    (role: AtendimentoRole) => {
      const routes = {
        recepcionist: '/atendimentos/fila',
        doctor: '/atendimentos/consultorio',
        monitor: '/atendimentos/monitor'
      };
      navigate(routes[role]);
    },
    [navigate]
  );

  useEffect(() => {
    const savedRole = localStorage.getItem(ROLE_STORAGE_KEY) as AtendimentoRole | null;

    if (savedRole && !isRoleSubRoute()) {
      setSelectedRole(savedRole);
      navigateToRole(savedRole);
    } else if (isRoleSubRoute()) {
      const path = location.pathname;
      if (path.includes('/fila')) {
        setSelectedRole('recepcionist');
        localStorage.setItem(ROLE_STORAGE_KEY, 'recepcionist');
      } else if (path.includes('/consultorio')) {
        setSelectedRole('doctor');
        localStorage.setItem(ROLE_STORAGE_KEY, 'doctor');
      } else if (path.includes('/monitor')) {
        setSelectedRole('monitor');
        localStorage.setItem(ROLE_STORAGE_KEY, 'monitor');
      }
    }
  }, [location.pathname, isRoleSubRoute, navigateToRole]);

  const handleRoleSelect = (role: AtendimentoRole) => {
    setSelectedRole(role);
    localStorage.setItem(ROLE_STORAGE_KEY, role);
    navigateToRole(role);
  };

  const clearRoleSelection = () => {
    setSelectedRole(null);
    localStorage.removeItem(ROLE_STORAGE_KEY);
    navigate('/atendimentos');
  };

  const roles = [
    {
      id: 'recepcionist' as AtendimentoRole,
      title: 'Recepção',
      description: 'Gerenciar fila de atendimento',
      icon: faUsers,
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      id: 'doctor' as AtendimentoRole,
      title: 'Consultório',
      description: 'Chamar e atender pacientes',
      icon: faUserMd,
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      id: 'monitor' as AtendimentoRole,
      title: 'Monitor',
      description: 'Exibir chamadas para pacientes',
      icon: faDesktop,
      color: 'bg-purple-500 hover:bg-purple-600'
    }
  ];

  if (isRoleSubRoute()) {
    return <Outlet />;
  }

  return (
    <main className="w-full flex-grow p-6">
      <div className="mx-auto space-y-6">
        <PageHeader
          title="Sistema de Atendimento"
          subtitle="Selecione o tipo de tela para continuar"
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {roles.map(role => (
            <div
              key={role.id}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className={`${role.color} p-8 text-white text-center`}>
                <FontAwesomeIcon icon={role.icon} className="w-16 h-16 mb-4" />
                <h3 className="text-2xl font-bold mb-2">{role.title}</h3>
                <p className="text-lg opacity-90">{role.description}</p>
              </div>
              <div className="p-6">
                <Button
                  variant="primary"
                  fullWidth
                  size="lg"
                  onClick={() => handleRoleSelect(role.id)}
                  className="text-lg py-3"
                >
                  Selecionar
                </Button>
              </div>
            </div>
          ))}
        </div>

        {selectedRole && (
          <div className="text-center">
            <p className="text-gray-600 mb-4">
              Papel selecionado anteriormente:{' '}
              <strong>
                {selectedRole === 'recepcionist'
                  ? 'Recepção'
                  : selectedRole === 'doctor'
                    ? 'Consultório'
                    : 'Monitor'}
              </strong>
            </p>
            <Button variant="outline" onClick={clearRoleSelection}>
              Limpar Seleção
            </Button>
          </div>
        )}
      </div>
    </main>
  );
};

export default AtendimentosPage;
